import React, { useState, useEffect } from "react";
import {
  TextField,
  PaginatedTable,
  TableRow,
  TableHeader,
  SortDirection,
  Cell,
  FadedCell,
  SecondaryButton,
  TextButton,
  useTranslations,
} from "@gf-easy-8/grmnext-foundation";
import styled from "styled-components";
import { useDispatch } from "react-redux";

import useStatusBar from "provider/StatusBarProvider";
import useSnackBar from "provider/SnackBarProvider";
import AddCircleIcon from "components/v2/icons/generated/AddCircleFilled";
import EditIcon from "components/v2/icons/generated/EditFilled";
import TrashIcon from "components/v2/icons/generated/TrashCanFilled";
import EyeIcon from "components/v2/icons/generated/EyeFilled";
import { Container, Label, SpinnerLoader } from "components/v2";
import { ProductRole, AppDispatch } from "types";
import { UserFormData } from "../type";
import { generateRowsWithId, sortAlphabetically } from "helper/utils";
import usePermission from "provider/PermissionProvider";
import { USER_MANAGEMENT_KEY } from "provider/PermissionProvider/permissions";
import { FormMode } from "constant";
import { deleteUserProduct } from "./actions";

const UserFormContainer = styled(Container)`
  margin-top: 5px;
`;
UserFormContainer.displayName = "UserFormContainer";

interface AccessDetailsContainerProps {
  errorMessage: string | undefined;
  hasProducts: boolean;
}

const AccessDetailsContainer = styled.div<AccessDetailsContainerProps>`
  margin-top: 20px;
  margin-bottom: 10px;
  padding-left: 1px;
  border: ${(props) =>
    `1px solid ${
      props.errorMessage && props.errorMessage !== ""
        ? props.theme.colours.global.alarm
        : props.theme.colours.global.lightFadedWhite
    }`};
  ${(props) =>
    props.hasProducts &&
    `
    & div:nth-child(3) {
      margin-right: 20px;
    }
    `}
`;
AccessDetailsContainer.displayName = "AccessDetailsContainer";

const ProductsActionCellContainer = styled("div")`
  text-align: right;
`;
ProductsActionCellContainer.displayName = "ProductsActionCellContainer";

const AddProductButtonContainer = styled("div")`
  text-align: right;
  padding: 15px;
`;
AddProductButtonContainer.displayName = "AddProductButtonContainer";

const StyledSecondaryButton = styled(SecondaryButton)`
  svg {
    margin: 0;
  }
`;
StyledSecondaryButton.displayName = "StyledSecondaryButton";

const AccessDetailsLabelContainer = styled("div")`
  margin: 10px 5px;
`;
AccessDetailsLabelContainer.displayName = "AccessDetailsLabelContainer";

type RowData = ProductRole & TableRow;

const UserDetails = ({
  user,
  form,
  formData,
  setFormData,
  formDataErrors,
  onChangeForm,
  setShowProductAccess,
  isLoading,
  setSelectedProduct,
  setRefreshUser,
}: {
  user: UserFormData;
  form: { disabled: boolean; mode: number };
  formData: UserFormData;
  setFormData: React.Dispatch<React.SetStateAction<UserFormData>>;
  formDataErrors: UserFormData;
  onChangeForm: (id: string, value: string | number) => void;
  setShowProductAccess: React.Dispatch<React.SetStateAction<boolean>>;
  isLoading: boolean;
  setSelectedProduct: React.Dispatch<React.SetStateAction<number>>;
  setRefreshUser: React.Dispatch<React.SetStateAction<number>>;
}) => {
  const { getTranslation } = useTranslations();
  const { permissionContext } = usePermission();
  const { hasFullPermission } = permissionContext.permissions;
  const dispatch: AppDispatch = useDispatch();
  const { statusBarContext } = useStatusBar();
  const { setStatusBarToLoading, setStatusBarToHealthy, setStatusBarToError } =
    statusBarContext.setStatusBar;
  const { snackBarContext } = useSnackBar();
  const { pushSuccessMessage, pushErrorMessage } = snackBarContext.modify;

  const getPage = (
    page: number,
    numberOfRows: number,
    sorting: { direction: SortDirection; column?: TableHeader<RowData> }
  ): RowData[] => {
    const rows: RowData[] = generateRowsWithId<ProductRole>(
      formData.userProducts!,
      "productId"
    );
    const sortedRows = sortAlphabetically(
      rows,
      sorting.column?.sortBy ?? "productName",
      sorting.direction === SortDirection.ascending
    );

    return sortedRows.slice(
      (page - 1) * numberOfRows,
      (page - 1) * numberOfRows + numberOfRows
    );
  };

  const [isDeleteButtonsDisabled, setIsDeleteButtonsDisabled] =
    useState<boolean>(false);
  const [page, setPage] = useState<RowData[]>([]);

  useEffect(() => {
    setPage(
      getPage(1, 3, {
        direction: SortDirection.ascending,
      })
    );
  }, [formData]);

  const headers: Array<TableHeader<RowData>> = [
    {
      id: "1",
      label: "Product",
      sortBy: "productName",
      colSpan: 2,
    },
  ];

  const onProductDelete = (productId: number) => () => {
    if (form.mode === FormMode.Add) {
      const userProducts =
        formData.userProducts &&
        formData.userProducts.filter(
          (userProduct) => userProduct.productId !== productId
        );
      const products =
        formData.products &&
        formData.products.filter(
          (userProduct) => userProduct.productId !== productId
        );

      setFormData({
        ...formData,
        products: products,
        userProducts: userProducts,
      });
    } else if (form.mode === FormMode.Edit) {
      setStatusBarToLoading();
      setIsDeleteButtonsDisabled(true);
      dispatch(deleteUserProduct(user.emailAddress, productId)).then(
        (response: boolean) => {
          if (response) {
            setStatusBarToHealthy();
            pushSuccessMessage(getTranslation("USER_MANAGEMENT:USER_UPDATED"));
            setRefreshUser(new Date().getTime());
          } else {
            setStatusBarToError();
            pushErrorMessage(
              getTranslation("USER_MANAGEMENT:DELETE_PRODUCT_ERROR")
            );
          }
          setIsDeleteButtonsDisabled(false);
        }
      );
    }
  };

  return isLoading ? (
    <SpinnerLoader />
  ) : (
    <>
      <UserFormContainer>
        <TextField
          id={"employeeId"}
          title="Employee Number"
          value={formData.employeeId}
          onChange={(val) => onChangeForm("employeeId", val)}
          disabled={form.disabled}
          invalid={formDataErrors.employeeId !== ""}
          errorMessage={formDataErrors.employeeId}
          type={"number"}
        />
        <TextField
          disabled
          id={"emailAddress"}
          title="Email"
          value={formData.emailAddress}
          invalid={formDataErrors.emailAddress !== ""}
          errorMessage={formDataErrors.emailAddress}
        />
        <TextField
          id={"department"}
          title="Department"
          value={formData.department}
          onChange={(val) => onChangeForm("department", val)}
          disabled={form.disabled}
          invalid={formDataErrors.department !== ""}
          errorMessage={formDataErrors.department}
        />
        <TextField
          id={"firstName"}
          title="First Name"
          value={formData.firstName}
          onChange={(val) => onChangeForm("firstName", val)}
          disabled={form.disabled}
          invalid={formDataErrors.firstName !== ""}
          errorMessage={formDataErrors.firstName}
        />
        <TextField
          id={"lastName"}
          title="Last Name"
          value={formData.lastName}
          onChange={(val) => onChangeForm("lastName", val)}
          disabled={form.disabled}
          invalid={formDataErrors.lastName !== ""}
          errorMessage={formDataErrors.lastName}
        />
      </UserFormContainer>
      <AccessDetailsContainer
        errorMessage={formDataErrors.productsError}
        hasProducts={
          (formData.userProducts && formData.userProducts.length > 0) || false
        }
      >
        <AccessDetailsLabelContainer>
          <Label>{getTranslation("USER_MANAGEMENT:ACCESS_DETAILS")}</Label>
        </AccessDetailsLabelContainer>
        {formData.userProducts && formData.userProducts.length > 0 ? (
          isDeleteButtonsDisabled ? (
            <SpinnerLoader />
          ) : (
            <PaginatedTable
              headers={headers}
              noOfRows={formData.userProducts.length || 0}
              currentPage={1}
              rowsPerPage={3}
              onChange={(
                pageNumber: number,
                rowsPerPage: number,
                sorting: {
                  direction: SortDirection;
                  column?: TableHeader<RowData>;
                }
              ) => {
                setPage(getPage(pageNumber, rowsPerPage, sorting));
              }}
              rows={page}
              rowsPerPageOptions={[3]}
              striped={true}
              condensed={true}
              noSpacing={true}
              render={({ productId, productName }) => (
                <React.Fragment key={productId}>
                  <Cell>{productName}</Cell>
                  <FadedCell>
                    <ProductsActionCellContainer>
                      <StyledSecondaryButton
                        onClick={() => {
                          setShowProductAccess(true);
                          setSelectedProduct(productId);
                        }}
                        icon={
                          hasFullPermission(productId, USER_MANAGEMENT_KEY) ? (
                            <EditIcon />
                          ) : (
                            <EyeIcon />
                          )
                        }
                      >
                        {""}
                      </StyledSecondaryButton>
                      <StyledSecondaryButton
                        onClick={onProductDelete(productId)}
                        icon={<TrashIcon />}
                        disabled={
                          !hasFullPermission(productId, USER_MANAGEMENT_KEY)
                        }
                      >
                        {""}
                      </StyledSecondaryButton>
                    </ProductsActionCellContainer>
                  </FadedCell>
                </React.Fragment>
              )}
            />
          )
        ) : (
          formDataErrors.productsError === "" && (
            <Label isParagraph isTextCentered>
              {getTranslation("USER_MANAGEMENT:NO_EXISTING_RPODUCTS")}
            </Label>
          )
        )}
        {formDataErrors.productsError !== "" && (
          <Label isParagraph isError isTextCentered>
            {formDataErrors.productsError}
          </Label>
        )}
        <AddProductButtonContainer>
          <TextButton
            onClick={() => {
              setShowProductAccess(true);
              setSelectedProduct(-1);
            }}
            primaryColor={true}
            isUnderlined={true}
            hasNormalText={true}
            icon={<AddCircleIcon />}
          >
            {getTranslation("USER_MANAGEMENT:ADD_PRODUCT_ACCESS")}
          </TextButton>
        </AddProductButtonContainer>
      </AccessDetailsContainer>
    </>
  );
};

export default UserDetails;
